import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Footer from "../../components/footer";
import ModuleHeader from "../../components/moduleHeader";
import { getProfile, setUpdateProfile } from "../../services/api";
import Notiflix from "notiflix";

function ProfileEdit() {
  const navigate = useNavigate();
  const { id: uuid } = useParams();
  
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    surname: "",
    phone: "",
    address: "",
    secretKey: "",
  });

  // Notiflix'i initialize et
  useEffect(() => {
    Notiflix.Notify.init({
      position: "top-right",
      timeout: 3000,
      clickToClose: true,
      pauseOnHover: true,
    });
  }, []);

  // Profil verilerini yükle
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const response = await getProfile();
        console.log("Profile data:", response);
        setProfile(response);
        if (response?.profile) {
          setProfileData(response);
          
          // Form verilerini doldur
          const profile = response.profile;
          const user = response.user;
          
          setFormData({
            name: profile.details?.first_name || user?.first_name || "",
            surname: profile.details?.last_name || user?.last_name || "",
            phone: profile.phone || "",
            address: profile.address || "",
            secretKey: profile?.secret_key || "",
          });
        }
      } catch (error) {
        console.error("Profil yüklenirken hata:", error);
        Notiflix.Notify.failure("Profil yüklenirken bir hata oluştu!");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // secretKey için sadece sayısal ve 4 hane sınırı
    if (name === "secretKey") {
      let v = value.replace(/\D/g, "");
      if (v.length > 4) v = v.substring(0, 4);
      setFormData((prev) => ({
        ...prev,
        secretKey: v,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Profil güncelleme fonksiyonu
  const handleSave = async () => {
    try {
      setSaving(true);
      
      const updateData = {
        name: formData.name,
        surname: formData.surname,
        phone: formData.phone,
        address: formData.address,
        secret_key: formData.secretKey,
      };

      const response = await setUpdateProfile(updateData);
      console.log("Profile updated:", response);
      
      Notiflix.Notify.success("Profil başarıyla güncellendi!");
      
      // Profil verilerini yeniden yükle
      const updatedProfile = await getProfile();
      setProfileData(updatedProfile);
      
    } catch (error) {
      console.error("Profil güncellenirken hata:", error);
      Notiflix.Notify.failure("Profil güncellenirken bir hata oluştu!");
    } finally {
      setSaving(false);
    }
  };

  // Loading durumunda loading ekranı göster
  if (loading || !profileData) {
    return (
      <div className="w-full h-screen min-h-screen flex items-center justify-center bg-[#fff]">
        <div className="flex flex-col items-center justify-center">
          <div className="relative mb-8">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-spin rounded-full h-24 w-24 border-4 border-transparent border-t-[#3DA7B5] border-r-[#3DA7B5] opacity-75"></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                className="animate-spin rounded-full h-16 w-16 border-[3px] border-transparent border-b-[#2a8a96] border-l-[#2a8a96] opacity-60"
                style={{
                  animationDirection: "reverse",
                  animationDuration: "1.5s",
                }}
              ></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#3DA7B5] via-[#2a8a96] to-[#3DA7B5] animate-pulse shadow-2xl"></div>
            </div>
          </div>
          <p className="text-xl font-semibold text-gray-800 animate-pulse">
            Yükleniyor...
          </p>
        </div>
      </div>
    );
  }

  const fullName = profileData?.user ? `${profileData.user.first_name} ${profileData.user.last_name}` : "";

  return (
    <div className="w-full h-screen min-h-screen flex items-stretch justify-stretch sm:items-center sm:justify-center bg-[#e8e9ed] relative">
      <div className="flex flex-col items-center w-full min-h-full bg-white shadow-xl border border-gray-200 rounded-none sm:max-w-[390px] sm:h-[700px] sm:rounded-3xl relative overflow-y-auto">
        {/* ModuleHeader */}
        <div className="relative z-30 w-full mb-2">
          <ModuleHeader
            userName={fullName}
            subtitle="Profil Düzenleme Modülüne Hoş Geldiniz"
            title="Profil Düzenle"
            description="Profil bilgilerinizi güncelleyin ve hesap ayarlarınızı yönetin."
            backgroundImage="/assets/settings.jpg"
            onExpandedChange={setIsHeaderExpanded}
            showToggle={true}
            autoHeight={true}
          />
        </div>

        <div className="w-full px-6 pb-20">
          <div className="flex flex-col gap-4 pt-4">
            {/* İsim */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                İsim
              </label>
              <input
                type="text"
                name="name"
                placeholder="İsim"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-[#3DA7B5] focus:border-[#3DA7B5] outline-none transition-all duration-200 text-gray-700 placeholder-gray-400"
              />
            </div>
            
            {/* Soyisim */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Soyisim
              </label>
              <input
                type="text"
                name="surname"
                placeholder="Soyisim"
                value={formData.surname}
                onChange={handleInputChange}
                className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-[#3DA7B5] focus:border-[#3DA7B5] outline-none transition-all duration-200 text-gray-700 placeholder-gray-400"
              />
            </div>
            
            {/* Telefon */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Telefon
              </label>
              <input
                type="text"
                name="phone"
                placeholder="Telefon numarası"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-[#3DA7B5] focus:border-[#3DA7B5] outline-none transition-all duration-200 text-gray-700 placeholder-gray-400"
              />
            </div>
            
            {/* Adres */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Adres
              </label>
              <input
                type="text"
                name="address"
                placeholder="Adres"
                value={formData.address}
                onChange={handleInputChange}
                className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-[#3DA7B5] focus:border-[#3DA7B5] outline-none transition-all duration-200 text-gray-700 placeholder-gray-400"
              />
            </div>

            {/* Secret Key */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Gizli Kod (4 Hane)
              </label>
              <input
                type="text"
                name="secretKey"
                placeholder="Örn: 1234"
                value={formData.secretKey}
                onChange={handleInputChange}
                maxLength={4}
                className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-[#3DA7B5] focus:border-[#3DA7B5] outline-none transition-all duration-200 text-gray-700 placeholder-gray-400"
              />
              {profileData?.profile?.secret_key && (
                <p className="text-xs text-gray-500 mt-1">
                  Kayıtlı gizli kod: <span className="font-semibold">{String(profileData.profile.secret_key)}</span>
                </p>
              )}
            </div>
            
            {/* Doğum Tarihi - Değiştirilemez */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Doğum Tarihi{" "}
                <span className="text-red-500 text-xs font-normal">
                  (Değiştirilemez)
                </span>
              </label>
              <input
                type="text"
                name="birthDate"
                placeholder="Gün / Ay / Yıl"
                value={profile?.profile?.birth_date || ""}
                disabled
                className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl bg-gray-100 text-gray-500 cursor-not-allowed outline-none"
              />
            </div>
            
            {/* Doğum Saati - Değiştirilemez */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Doğum Saati{" "}
                <span className="text-red-500 text-xs font-normal">
                  (Değiştirilemez)
                </span>
              </label>
              <input
                type="text"
                name="birthTime"
                placeholder="00:00"
                value={profile?.profile?.birth_time ? profile.profile.birth_time.split(":")[0] + ":" + profile.profile.birth_time.split(":")[1] : ""}
                disabled
                className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl bg-gray-100 text-gray-500 cursor-not-allowed outline-none"
              />
            </div>
            
            {/* Doğum Yeri - Değiştirilemez */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Doğum Yeri{" "}
                <span className="text-red-500 text-xs font-normal">
                  (Değiştirilemez)
                </span>
              </label>
              <input
                type="text"
                name="birthPlace"
                placeholder="Ülke Seç"
                value={profile?.profile?.details?.birth_place || ""}
                disabled
                className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl bg-gray-100 text-gray-500 cursor-not-allowed outline-none"
              />
            </div>

            {/* Kaydet Butonu */}
            <button
              onClick={handleSave}
              disabled={saving}
              className={`w-full py-4 px-6 rounded-xl font-semibold text-white transition-all duration-300 mt-4 ${
                saving 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-gradient-to-r from-[#3DA7B5] to-[#2a8a96] hover:from-[#2a8a96] hover:to-[#3DA7B5] hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]'
              }`}
            >
              {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
            </button>
          </div>
        </div>

        <Footer type={0} uuid={uuid} />
      </div>
    </div>
  );
}

export default ProfileEdit;
